import {
  endOfMonth,
  endOfWeek,
  getLocalTimeZone,
  startOfMonth,
  startOfWeek,
  today,
} from '@internationalized/date';
import { useControlledState } from '@react-stately/utils';
import { Calendar as CalendarIcon } from '@untitledui/icons';
import { useEffect, useMemo, useState } from 'react';
import { useDateFormatter } from 'react-aria';
import type {
  DateRangePickerProps as AriaDateRangePickerProps,
  DateValue,
} from 'react-aria-components';
import {
  Button as AriaButton,
  DateRangePicker as AriaDateRangePicker,
  Dialog as AriaDialog,
  Group as AriaGroup,
  Popover as AriaPopover,
  useLocale,
} from 'react-aria-components';

import { StackingLayer, useStackingZIndex } from '@/contexts/StackingContext';
import { Button } from '@/lib/base/buttons/button';
import {
  SELECT_TRIGGER_BASE,
  type SelectSize,
  sizes,
} from '@/lib/base/forms/select/select';
import { cx } from '@/utils/cx.ts';

import { DateInput } from './date-input.tsx';
import { RangeCalendar } from './range-calendar.tsx';
import { RangePresetButton } from './range-preset.tsx';

interface DateRangePickerProps extends AriaDateRangePickerProps<DateValue> {
  /** The function to call when the apply button is clicked. */
  onApply?: () => void;
  /** The function to call when the cancel button is clicked. */
  onCancel?: () => void;
  /**
   * Trigger size, on the shared field-control scale — the same prop and values
   * as Input, Select and DatePicker. Defaults to `'sm'` (40px).
   */
  size?: SelectSize;
  /**
   * Trigger text when no range is selected. Defaults to `'Select dates'`.
   *
   * Override it where "no range" is a MEANING rather than a missing answer. On
   * a filter bar, an empty picker is not an unanswered question — it is the
   * statement "no date filter is narrowing this", and an imperative there
   * reads as though the data is waiting on the viewer.
   */
  placeholder?: string;
}

export const DateRangePicker = ({
  value: valueProp,
  defaultValue,
  onChange,
  onApply,
  onCancel,
  size = 'sm',
  placeholder = 'Select dates',
  ...props
}: DateRangePickerProps) => {
  const { locale } = useLocale();
  const formatter = useDateFormatter({
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const zIndex = useStackingZIndex();
  // Committed value (synced with parent)
  const [value, setValue] = useControlledState(
    valueProp,
    defaultValue || null,
    onChange
  );

  // Draft value (only committed on Apply)
  const [draftValue, setDraftValue] = useState<{
    start: DateValue;
    end: DateValue;
  } | null>(value);
  const [focusedValue, setFocusedValue] = useState<DateValue | null>(null);

  // Sync draftValue when parent-controlled value changes
  useEffect(() => {
    setDraftValue(value);
  }, [value]);

  const formattedStartDate = value?.start
    ? formatter.format(value.start.toDate(getLocalTimeZone()))
    : 'Select date';
  const formattedEndDate = value?.end
    ? formatter.format(value.end.toDate(getLocalTimeZone()))
    : 'Select date';

  const presets = useMemo(() => {
    // Compute current date inside useMemo so it's always fresh
    const now = today(getLocalTimeZone());
    return {
      today: { label: 'Today', value: { start: now, end: now } },
      yesterday: {
        label: 'Yesterday',
        value: {
          start: now.subtract({ days: 1 }),
          end: now.subtract({ days: 1 }),
        },
      },
      thisWeek: {
        label: 'This week',
        value: { start: startOfWeek(now, locale), end: endOfWeek(now, locale) },
      },
      lastWeek: {
        label: 'Last week',
        value: {
          start: startOfWeek(now, locale).subtract({ weeks: 1 }),
          end: endOfWeek(now, locale).subtract({ weeks: 1 }),
        },
      },
      thisMonth: {
        label: 'This month',
        value: { start: startOfMonth(now), end: endOfMonth(now) },
      },
      lastMonth: {
        label: 'Last month',
        value: {
          start: startOfMonth(now).subtract({ months: 1 }),
          end: endOfMonth(now).subtract({ months: 1 }),
        },
      },
      thisYear: {
        label: 'This year',
        value: {
          start: startOfMonth(now.set({ month: 1 })),
          end: endOfMonth(now.set({ month: 12 })),
        },
      },
      lastYear: {
        label: 'Last year',
        value: {
          start: startOfMonth(now.set({ month: 1 }).subtract({ years: 1 })),
          end: endOfMonth(now.set({ month: 12 }).subtract({ years: 1 })),
        },
      },
      allTime: {
        label: 'All time',
        value: {
          start: now.set({ year: 2000, month: 1, day: 1 }),
          end: now,
        },
      },
    };
  }, [locale]);

  return (
    <AriaDateRangePicker
      aria-label='Date range picker'
      shouldCloseOnSelect={false}
      {...props}
      value={draftValue}
      onChange={setDraftValue}
    >
      <AriaGroup>
        {/* Field-styled trigger — same base classes and scale as Select and
            DatePicker, so all of them line up at a given `size`. */}
        <AriaButton
          isDisabled={props.isDisabled}
          className={cx(
            SELECT_TRIGGER_BASE,
            'gap-2 text-left text-primary',
            sizes[size].root,
            'focus-visible:ring-2 focus-visible:ring-brand',
            'disabled:cursor-not-allowed disabled:bg-disabled_subtle disabled:text-disabled'
          )}
        >
          <CalendarIcon
            aria-hidden='true'
            className={cx('shrink-0 text-fg-quaternary', sizes[size].icon)}
          />
          <span
            data-text
            className={cx('flex-1 truncate', !value && 'text-placeholder')}
          >
            {!value
              ? placeholder
              : `${formattedStartDate} – ${formattedEndDate}`}
          </span>
        </AriaButton>
      </AriaGroup>
      <AriaPopover
        placement='bottom right'
        offset={8}
        containerPadding={16}
        onOpenChange={isOpen => {
          // Sync draft with committed value when opening
          if (isOpen) {
            setDraftValue(value);
          }
        }}
        className={({ isEntering, isExiting }) =>
          cx(
            'origin-(--trigger-anchor-point) will-change-transform',
            // Visual container + height clamp belong on the popover, not the
            // inner Dialog — see the note in date-picker.tsx. react-aria writes
            // the genuinely available height onto this element; a fixed
            // max-height on the Dialog overrides that budget and pushes the
            // footer below the fold (the bottom-cutoff bug).
            'flex flex-col overflow-hidden rounded-2xl bg-primary shadow-xl ring ring-secondary_alt',
            isEntering &&
              'duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5',
            isExiting &&
              'duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5'
          )
        }
        style={{ zIndex }}
      >
        <StackingLayer>
          <AriaDialog className='flex min-h-0 flex-1 focus:outline-hidden'>
            {({ close }) => (
              <>
                <div className='hidden w-38 shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-solid border-secondary p-3 lg:flex'>
                  {Object.values(presets).map(preset => (
                    <RangePresetButton
                      key={preset.label}
                      value={preset.value}
                      currentValue={draftValue}
                      onClick={() => {
                        setDraftValue(preset.value);
                        setFocusedValue(preset.value.start);
                      }}
                    >
                      {preset.label}
                    </RangePresetButton>
                  ))}
                </div>
                <div className='flex min-h-0 flex-1 flex-col'>
                  {/* Scroll region — the footer below stays pinned so Apply is
                      always reachable, even in a short viewport. */}
                  <div className='min-h-0 flex-1 overflow-y-auto'>
                    <RangeCalendar
                      focusedValue={focusedValue}
                      onFocusChange={setFocusedValue}
                      presets={{
                        lastWeek: presets.lastWeek,
                        lastMonth: presets.lastMonth,
                        lastYear: presets.lastYear,
                      }}
                    />
                  </div>
                  <div className='flex shrink-0 justify-between gap-3 border-t border-secondary p-4'>
                    <div className='hidden items-center gap-3 md:flex'>
                      <DateInput slot='start' className='w-36' />
                      <div className='text-md text-quaternary'>–</div>
                      <DateInput slot='end' className='w-36' />
                    </div>
                    <div className='grid w-full grid-cols-2 gap-3 md:flex md:w-auto'>
                      <Button
                        size='md'
                        color='secondary'
                        onClick={() => {
                          // Revert draft to committed value
                          setDraftValue(value);
                          onCancel?.();
                          close();
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size='md'
                        color='primary'
                        onClick={() => {
                          // Commit draft to parent
                          setValue(draftValue);
                          onApply?.();
                          close();
                        }}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </AriaDialog>
        </StackingLayer>
      </AriaPopover>
    </AriaDateRangePicker>
  );
};
